export type TimeUnit = 'mins' | 'hrs';

export interface TimeConversionResult {
  value: number | undefined;
  unit: TimeUnit;
  displayValue: number | undefined;
}

/**
 * Converts time between minutes and hours
 * @param value - The numeric value to convert
 * @param fromUnit - The current unit of the value
 * @param toUnit - The target unit to convert to
 * @returns Object containing converted value, unit, and display value
 */
export const convertTime = (
  value: number | undefined,
  fromUnit: TimeUnit,
  toUnit: TimeUnit,
): TimeConversionResult => {
  // Handle undefined values
  if (value === null || value === undefined) {
    return {
      value: undefined,
      unit: toUnit,
      displayValue: undefined,
    };
  }

  if (fromUnit === toUnit) {
    return {
      value,
      unit: toUnit,
      displayValue: value,
    };
  }

  if (fromUnit === 'mins' && toUnit === 'hrs') {
    // Convert minutes to hours
    const hoursValue = value / 60;
    return {
      value: hoursValue,
      unit: 'hrs',
      displayValue: hoursValue,
    };
  }

  if (fromUnit === 'hrs' && toUnit === 'mins') {
    // Convert hours to minutes
    const minutesValue = value * 60;
    return {
      value: minutesValue,
      unit: 'mins',
      displayValue: minutesValue,
    };
  }

  return {
    value,
    unit: fromUnit,
    displayValue: value,
  };
};

/**
 * Formats time unit for display
 * @param unit - The time unit
 * @returns Formatted string for display
 */
export const formatTimeUnit = (unit: TimeUnit): string => {
  return unit === 'mins' ? 'Mins' : 'Hrs';
};

/**
 * Parses time unit from string
 * @param unitString - String representation of time unit
 * @returns TimeUnit enum value
 */
export const parseTimeUnit = (unitString: string): TimeUnit => {
  const normalized = unitString.toLowerCase().trim();
  return normalized === 'hrs' || normalized === 'hours' ? 'hrs' : 'mins';
};
